import type { SharedSelection } from '@/components/ui';
import {
  Button,
  ButtonGroup,
  Select,
  SelectItem,
  Tooltip,
} from '@/components/ui';
import { useMemo, type ReactElement } from 'react';
import { Icon } from '@iconify/react';

export type SequentialPaginationProps = {
  page: number;
  itemsPerPage: number;
  allowItemsPerPage?: number[];
  onPageChange: (page: number) => void;
  onItemsPerPageChange?: (value: number) => void;
  count: number;
  className?: string;
};

function Container({
  rounded = true,
  className,
  children,
}: {
  rounded?: boolean;
  className?: string;
  children?: ReactElement | ReactElement[] | string;
}) {
  return (
    <div
      className={`${className || ''} flex flex-row items-center ${rounded ? 'rounded-medium' : ''} bg-default-100 px-4 py-2 text-sm`}
    >
      {children}
    </div>
  );
}

export function AbsolutePagingInfo({
  page,
  itemsPerPage,
  count,
  className,
}: {
  page: number;
  itemsPerPage: number;
  count: number;
  className?: string;
}) {
  const info = getInfo({ page, itemsPerPage, count });

  return <Container className={className}>{info}</Container>;
}

function getInfo({
  page,
  itemsPerPage,
  count,
}: {
  page: number;
  itemsPerPage: number;
  count: number;
}) {
  const offsetStart = page < 1 ? 0 : (page - 1) * itemsPerPage + 1;
  const offsetEnd = Math.min(page * itemsPerPage, count);
  const startString = Number(offsetStart).toLocaleString();
  const endString = Number(offsetEnd).toLocaleString();
  const countString = Number(count).toLocaleString();
  const info =
    page === 0 || count === 0
      ? ''
      : `Showing ${startString}-${endString} of ${countString} results`;

  return info;
}

export function SequentialPagination({
  page,
  itemsPerPage,
  allowItemsPerPage,
  onPageChange,
  onItemsPerPageChange,
  count,
  className,
}: SequentialPaginationProps) {
  const atStart = page === 1;
  const atEnd = page * itemsPerPage >= count;
  const prevPage = () => {
    if (atStart) {
      return;
    }

    onPageChange(page - 1);
  };

  const nextPage = () => {
    if (atEnd) {
      return;
    }

    onPageChange(page + 1);
  };

  const info = getInfo({ page, itemsPerPage, count });
  const itemsPerPageList = useMemo(() => {
    if (!allowItemsPerPage) {
      return undefined;
    }

    return allowItemsPerPage.map((item) => ({
      value: item,
      text: String(item),
    }));
  }, [allowItemsPerPage]);
  const itemsPerChangeHandler = (keys: SharedSelection) => {
    if (!onItemsPerPageChange) {
      return;
    }

    const value = parseInt(keys.currentKey || '0', 10);
    onItemsPerPageChange(value);
  };

  return (
    <div
      className={`${className} flex flex-row items-center rounded-medium bg-default-100 pl-4 text-sm`}
    >
      <span className='mr-2 text-nowrap'>{info}</span>
      <ButtonGroup className='flex-grow'>
        <Button
          isIconOnly
          variant='light'
          className='w-10 bg-transparent text-default-600 hover:bg-slate-100 disabled:text-gray-400'
          disabled={atStart}
          onPress={prevPage}
          aria-label='Previous page'
        >
          <Icon icon='mdi:chevron-left' width={16} height={16} />
        </Button>
        <Button
          isIconOnly
          variant='light'
          className='w-10 bg-transparent text-default-600 hover:bg-slate-100 disabled:text-gray-400'
          disabled={atEnd}
          onPress={nextPage}
          aria-label='Next page'
        >
          <Icon icon='mdi:chevron-right' width={16} height={16} />
        </Button>
      </ButtonGroup>
      {itemsPerPageList && (
        <>
          <Tooltip content='Items Per Page' offset={48}>
            <Select
              className='w-20'
              variant='flat'
              items={itemsPerPageList}
              selectedKeys={[String(itemsPerPage)]}
              onSelectionChange={itemsPerChangeHandler}
            >
              {itemsPerPageList.map((item) => (
                <SelectItem key={item.value}>{item.text}</SelectItem>
              ))}
            </Select>
          </Tooltip>
        </>
      )}
    </div>
  );
}

/** Page numbers to display: 1, 2, 3, ..., 3694 with ellipsis for gaps */
function getPageNumbers(
  currentPage: number,
  totalPages: number,
  maxVisible: number = 5,
): (number | 'ellipsis')[] {
  if (totalPages <= 0) {
    return [];
  }
  if (totalPages <= maxVisible + 2) {
    return Array.from({ length: totalPages }, (_, i) => i + 1);
  }
  const result: (number | 'ellipsis')[] = [];
  const showLeft = currentPage > 2;
  const showRight = currentPage < totalPages - 1;
  result.push(1);
  if (showLeft) {
    result.push('ellipsis');
  }
  const low = Math.max(2, currentPage - 1);
  const high = Math.min(totalPages - 1, currentPage + 1);
  for (let p = low; p <= high; p++) {
    if (p !== 1 && p !== totalPages) {
      result.push(p);
    }
  }
  if (showRight) {
    result.push('ellipsis');
  }
  if (totalPages > 1) {
    result.push(totalPages);
  }
  return result;
}

/**
 * Pagination2: same behavior as SequentialPagination, with image-style layout:
 * "Showing 1-20 of 73,863 results" (bold numbers) + prev/next arrows + page number buttons (current in blue).
 */
export function Pagination2({
  page,
  itemsPerPage,
  allowItemsPerPage,
  onPageChange,
  onItemsPerPageChange,
  count,
  className,
}: SequentialPaginationProps) {
  const atStart = page <= 1;
  const totalPages = Math.max(1, Math.ceil(count / itemsPerPage));
  const atEnd = page >= totalPages;

  const prevPage = () => {
    if (!atStart) {
      onPageChange(page - 1);
    }
  };

  const nextPage = () => {
    if (!atEnd) {
      onPageChange(page + 1);
    }
  };

  const offsetStart = page < 1 ? 0 : (page - 1) * itemsPerPage + 1;
  const offsetEnd = Math.min(page * itemsPerPage, count);
  const startString = Number(offsetStart).toLocaleString();
  const endString = Number(offsetEnd).toLocaleString();
  const countString = Number(count).toLocaleString();

  const pageNumbers = useMemo(
    () => getPageNumbers(page, totalPages),
    [page, totalPages],
  );

  const itemsPerPageList = useMemo(() => {
    if (!allowItemsPerPage) {
      return undefined;
    }
    return allowItemsPerPage.map((item) => ({
      value: item,
      text: String(item),
    }));
  }, [allowItemsPerPage]);

  const itemsPerChangeHandler = (keys: SharedSelection) => {
    if (!onItemsPerPageChange) {
      return;
    }
    const value = parseInt(keys.currentKey || '0', 10);
    onItemsPerPageChange(value);
  };

  const isEmpty = count === 0 || totalPages <= 0;

  return (
    <div
      className={`${className ?? ''} flex flex-wrap items-center justify-between gap-3 rounded-lg border-t border-slate-200 bg-white px-4 py-3 text-sm text-slate-600`}
    >
      <div className='flex items-center gap-3'>
        <span className='whitespace-nowrap'>
          {isEmpty ? (
            'No results'
          ) : (
            <>
              Showing{' '}
              <span className='font-semibold text-slate-800'>
                {startString}-{endString}
              </span>
              {' of '}
              <span className='font-semibold text-slate-800'>
                {countString}
              </span>
              {' results'}
            </>
          )}
        </span>
        {itemsPerPageList && (
          <Tooltip content='Items per page' offset={48}>
            <Select
              className='w-20'
              variant='flat'
              size='sm'
              items={itemsPerPageList}
              selectedKeys={[String(itemsPerPage)]}
              onSelectionChange={itemsPerChangeHandler}
            >
              {itemsPerPageList.map((item) => (
                <SelectItem key={item.value}>{item.text}</SelectItem>
              ))}
            </Select>
          </Tooltip>
        )}
      </div>

      <div className='flex items-center gap-1'>
        <Button
          isIconOnly
          variant='light'
          size='sm'
          className='min-w-8 text-slate-600 hover:bg-slate-100 disabled:opacity-40'
          disabled={atStart}
          onPress={prevPage}
          aria-label='Previous page'
        >
          <Icon icon='mdi:chevron-left' width={20} height={20} />
        </Button>

        <div className='flex items-center gap-1'>
          {pageNumbers.map((item, index) => {
            if (item === 'ellipsis') {
              return (
                <span
                  key={`ellipsis-${index}`}
                  className='px-2 text-slate-400'
                  aria-hidden
                >
                  …
                </span>
              );
            }
            const isCurrent = item === page;
            return (
              <Button
                key={item}
                size='sm'
                variant={isCurrent ? 'solid' : 'light'}
                color={isCurrent ? 'primary' : 'default'}
                className={
                  isCurrent
                    ? 'min-w-8 font-semibold'
                    : 'min-w-8 bg-transparent text-slate-600 hover:bg-slate-100'
                }
                onPress={() => onPageChange(item)}
                aria-label={
                  isCurrent ? `Page ${item} (current)` : `Page ${item}`
                }
                aria-current={isCurrent ? 'page' : undefined}
              >
                {item.toLocaleString()}
              </Button>
            );
          })}
        </div>

        <Button
          isIconOnly
          variant='light'
          size='sm'
          className='min-w-8 text-slate-600 hover:bg-slate-100 disabled:opacity-40'
          disabled={atEnd}
          onPress={nextPage}
          aria-label='Next page'
        >
          <Icon icon='mdi:chevron-right' width={20} height={20} />
        </Button>
      </div>
    </div>
  );
}
